import React, { useEffect, useState } from 'react';
import { X, IndianRupee, Truck, Route, Wallet, TrendingUp, Clock, ArrowDownToLine, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { driverApi } from '../lib/api';
import { toast } from '../hooks/use-toast';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const demoEarnings = {
  driver_name: 'Sohan Lal',
  total_trips: 34,
  total_earnings: 685000,
  month_trips: 12,
  month_earnings: 428500,
  month_km: 12480,
  available_withdraw: 148500,
  pending_settlement: 65000,
  active_trips: 3,
  weekly: [
    { day: 'Mon', date: '06 Jul', trips: 1, earnings: 42000 },
    { day: 'Tue', date: '07 Jul', trips: 2, earnings: 78000 },
    { day: 'Wed', date: '08 Jul', trips: 1, earnings: 32000 },
    { day: 'Thu', date: '09 Jul', trips: 0, earnings: 0 },
    { day: 'Fri', date: '10 Jul', trips: 3, earnings: 145000 },
    { day: 'Sat', date: '11 Jul', trips: 2, earnings: 68000 },
    { day: 'Sun', date: '12 Jul', trips: 1, earnings: 63500 },
  ],
};

export default function DriverEarnings({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    driverApi.earnings().then(r => {
      // Use demo data if no real trips
      if (!r || r.total_trips === 0) setData({ ...demoEarnings, driver_name: r?.driver_name || demoEarnings.driver_name });
      else setData(r);
    }).catch(() => setData(demoEarnings)).finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const maxWeekly = data ? Math.max(...data.weekly.map(w => w.earnings), 1) : 1;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0B2545] z-10 flex items-center justify-between p-4 text-white">
          <div>
            <h3 className="font-bold">Earnings</h3>
            <p className="text-[11px] text-white/70">{data?.driver_name}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>

        {loading || !data ? (
          <div className="py-16 text-center text-gray-400"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Withdraw card */}
            <div className="bg-gradient-to-br from-[#00A651] to-[#00c060] rounded-2xl p-5 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
              <p className="text-xs text-white/80 uppercase">Available to Withdraw</p>
              <p className="text-4xl font-extrabold font-[Poppins] my-1">{formatINR(data.available_withdraw)}</p>
              <p className="text-xs text-white/70 mb-4">Pending settlement: {formatINR(data.pending_settlement)}</p>
              <Button onClick={() => toast({ title: 'Withdrawal initiated', description: `${formatINR(data.available_withdraw)} will hit your bank in ~2 hours via IMPS.` })} className="bg-white text-[#00A651] hover:bg-white/90 h-10">
                <ArrowDownToLine className="w-4 h-4 mr-2" /> Withdraw to Bank
              </Button>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white border border-gray-100 rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1"><Truck className="w-4 h-4 text-[#00A651]" /><p className="text-[10px] text-gray-500 uppercase">Trips (Total)</p></div>
                <p className="text-xl font-extrabold text-[#0B2545]">{data.total_trips}</p>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1"><TrendingUp className="w-4 h-4 text-[#00A651]" /><p className="text-[10px] text-gray-500 uppercase">Total Earned</p></div>
                <p className="text-xl font-extrabold text-[#0B2545]">{formatINR(data.total_earnings)}</p>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1"><Route className="w-4 h-4 text-[#B37800]" /><p className="text-[10px] text-gray-500 uppercase">KM This Month</p></div>
                <p className="text-xl font-extrabold text-[#0B2545]">{data.month_km.toLocaleString('en-IN')} km</p>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1"><Clock className="w-4 h-4 text-[#0B2545]" /><p className="text-[10px] text-gray-500 uppercase">Active</p></div>
                <p className="text-xl font-extrabold text-[#0B2545]">{data.active_trips}</p>
              </div>
            </div>

            {/* Month card */}
            <div className="bg-white border border-gray-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-[#0B2545]">This Month</p>
                <Badge className="bg-[#00A651]/10 text-[#00A651] border-0">{data.month_trips} trips</Badge>
              </div>
              <p className="text-2xl font-extrabold text-[#00A651] font-[Poppins]">{formatINR(data.month_earnings)}</p>
              <p className="text-xs text-gray-500 mt-0.5">Avg per trip: {data.month_trips > 0 ? formatINR(Math.round(data.month_earnings / data.month_trips)) : '—'}</p>
            </div>

            {/* Weekly chart */}
            <div className="bg-white border border-gray-100 rounded-xl p-4">
              <p className="text-sm font-bold text-[#0B2545] mb-3">Last 7 Days</p>
              <div className="flex items-end justify-around gap-1 h-32">
                {data.weekly.map((w, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="relative w-full flex items-end justify-center h-24">
                      <div className="w-full brand-gradient rounded-t transition-all" style={{ height: `${Math.max(4, (w.earnings / maxWeekly) * 100)}%` }} title={formatINR(w.earnings)}></div>
                    </div>
                    <span className="text-[10px] text-gray-500 font-semibold">{w.day}</span>
                    <span className="text-[9px] text-gray-400">{w.trips}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-[10px] text-gray-500 text-center">Settlements happen T+2 for verified drivers. First withdrawal free, subsequent ₹5 flat fee.</p>
          </div>
        )}
      </div>
    </div>
  );
}
